/* Crooze: Google reviews carousel (sections/crooze-google-reviews.liquid).
 *
 * Reads the Google Reviews app's (Reputon's) public feed and draws the cards itself.
 * - Hidden by default: shown only once the feed has loaded and passed the checks below.
 *   A failed, slow or changed feed, or a missing stylesheet, keeps the section hidden.
 * - Cards are a fixed height; "Read more" opens a <dialog>, so the page never moves.
 * - The row is a native scroller with snap points, so phones swipe it normally.
 * - It loops by appending one copy of the cards: once the row is half a card into the
 *   copy, it is moved back by exactly one set width, which looks identical.
 * - Auto-scroll moves one card at a time and waits while the mouse is over the row,
 *   after a swipe or arrow click, while a review is open, and while off-screen.
 * - With "Load only when near the screen" on, nothing is fetched or drawn until the
 *   section is within one and a half screens of view, judged from a second after load.
 * - The Grid layout (for a page about reviews) shows a set number of rows of bigger cards,
 *   a Show more button that adds the same again, and a Leave a review button. No scrolling,
 *   no loop. Once every review in the feed is showing, a link to all of them on Google.
 */
(() => {
  if (customElements.get('crz-google-reviews')) return;

  const rtf = new Intl.RelativeTimeFormat('en-AU', { numeric: 'auto' });
  const nf = new Intl.NumberFormat('en-AU');
  const df = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const UNITS = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400]];
  const isHttps = (url) => typeof url === 'string' && url.startsWith('https://');

  // The section is hidden until this script shows it, and it stays hidden if anything is off.
  // The feed is Reputon's and undocumented, so it could change shape without warning:
  // anything that does not look exactly as expected counts as broken.
  const MIN_REVIEWS = 3; // fewer than this would look broken
  const FEED_TIMEOUT = 8000; // a feed that has not answered by then counts as broken
  const validBusiness = (b) => !!b && typeof b === 'object'
    && Number.isFinite(b.rating) && b.rating >= 1 && b.rating <= 5
    && Number.isInteger(b.reviewsNumber) && b.reviewsNumber > 0
    && Array.isArray(b.reviews);
  // `hide` must be present and false: if the field were renamed, reviews hidden in the app
  // would otherwise show up here.
  const validReview = (r) => !!r && typeof r === 'object' && r.hide === false
    && typeof r.text === 'string' && r.text.trim() !== ''
    && typeof r.authorName === 'string' && r.authorName.trim() !== ''
    && Number.isInteger(r.rating) && r.rating >= 1 && r.rating <= 5
    && Number.isFinite(r.time) && r.time > 0;
  const photosOf = (r) => (Array.isArray(r.images) ? r.images.filter((p) => p && isHttps(p.url)) : []);
  const replyOf = (r) => (typeof r.reply === 'string' && r.reply.trim() ? r.reply : '');

  const ago = (seconds) => {
    const diff = seconds - Date.now() / 1000;
    for (const [unit, size] of UNITS) {
      if (Math.abs(diff) >= size) return rtf.format(Math.trunc(diff / size), unit);
    }
    return rtf.format(0, 'day');
  };

  // Review text is written by the public, so everything goes in through textContent.
  const h = (tag, props = {}, ...kids) => {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(props)) {
      if (value == null || value === false) continue;
      if (key === 'class') node.className = value;
      else if (key === 'text') node.textContent = value;
      else node.setAttribute(key, value === true ? '' : value);
    }
    node.append(...kids.filter(Boolean));
    return node;
  };

  const stars = (rating) => {
    const r = Math.max(0, Math.min(5, Number(rating) || 0));
    const node = h('span', { class: 'crz-stars', role: 'img', 'aria-label': `Rated ${r} out of 5` });
    node.style.setProperty('--r', r);
    return node;
  };

  const avatar = (name, url) => {
    const node = h('span', { class: 'crz-avatar', 'aria-hidden': 'true' });
    node.dataset.initial = (name || '?').trim().charAt(0).toUpperCase();
    if (isHttps(url)) {
      node.append(h('img', { src: url, alt: '', width: 40, height: 40, loading: 'lazy', decoding: 'async', referrerpolicy: 'no-referrer' }));
    }
    return node;
  };

  class CrzGoogleReviews extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.track = this.querySelector('[data-track]');
      this.dialog = this.querySelector('[data-dialog]');
      this.delay = Math.max(3, Number(this.dataset.delay) || 5) * 1000;
      this.grid = this.dataset.layout === 'grid';
      this.autoplay = !this.grid && this.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Wait until the section is within one and a half screens of view: no feed, photos or
      // drawing for shoppers who never scroll that far. The theme editor loads at once.
      // The page above can still grow after this runs — Searchanise draws collection grids
      // seconds later, and until then a section 10,000 px down sits just below the first
      // screen — so distance is judged only from a second after the page has loaded.
      if (this.dataset.start === 'near' && 'IntersectionObserver' in window && !window.Shopify?.designMode) {
        const watch = () => {
          if (!this.isConnected) return;
          this.nearIo = new IntersectionObserver((entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            this.nearIo.disconnect();
            this.load();
          }, { rootMargin: '150% 0px' });
          this.nearIo.observe(this);
        };
        const settle = () => setTimeout(watch, 1000);
        if (document.readyState === 'complete') settle();
        else window.addEventListener('load', settle, { once: true });
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.nearIo?.disconnect();
      this.io?.disconnect();
      this.ro?.disconnect();
    }

    async load() {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), FEED_TIMEOUT);
        let data;
        try {
          const res = await fetch(this.dataset.feed, { credentials: 'omit', signal: controller.signal });
          if (!res.ok) throw new Error(`feed returned ${res.status}`);
          data = await res.json();
        } finally {
          clearTimeout(timeout);
        }
        const business = Array.isArray(data?.business) ? data.business[0] : null;
        if (!validBusiness(business)) throw new Error('feed is not in the expected shape');
        const reviews = business.reviews.filter(validReview).sort((a, b) => b.time - a.time);
        if (reviews.length < MIN_REVIEWS) throw new Error(`feed has only ${reviews.length} usable reviews`);
        // Without the stylesheet the cards would show as a bare list.
        if (getComputedStyle(this.track).display !== (this.grid ? 'grid' : 'flex')) throw new Error('stylesheet did not load');
        this.business = business;
        this.reviews = reviews;
        this.render();
      } catch (err) {
        this.fail(err);
      }
    }

    // Broken in any way: stop, and hide the whole section (the theme editor shows a note).
    fail(err) {
      console.warn('Google reviews carousel hidden:', err);
      clearTimeout(this.timer);
      this.io?.disconnect();
      this.ro?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      this.dataset.state = 'error';
      if (!window.Shopify?.designMode) (this.closest('.shopify-section') || this).hidden = true;
    }

    googleG() {
      return this.querySelector('[data-google-g]')?.content.firstElementChild?.cloneNode(true) || null;
    }

    render() {
      this.fillPlate();
      const cards = [];
      const summary = Array.isArray(this.business.summary?.items)
        ? this.business.summary.items.filter((t) => typeof t === 'string' && t.trim())
        : [];
      if (this.dataset.summary === 'true' && summary.length) cards.push(this.summaryCard(summary));
      this.reviews.forEach((review, i) => cards.push(this.card(review, i)));
      this.track.replaceChildren(...cards);
      if (this.grid) {
        this.bindCommon();
        this.renderGrid();
        this.dataset.state = 'ready';
        return;
      }
      this.bind();
      // Lay out now rather than waiting for the first ResizeObserver callback, which a
      // background tab may not deliver. A zero width (hidden) is picked up by the observer.
      this.onResize();
      // Only now, with everything built, does the section become visible.
      this.dataset.state = 'ready';
    }

    fillPlate() {
      const plate = this.querySelector('[data-plate]');
      if (!plate) return;
      const b = this.business;
      if (isHttps(b.url)) plate.href = b.url;
      if (b.name) plate.querySelector('[data-plate-name]').textContent = b.name;
      const rating = Number(b.rating) || 0;
      plate.querySelector('[data-plate-rating]').textContent = rating.toFixed(1);
      const plateStars = plate.querySelector('[data-plate-stars]');
      plateStars.style.setProperty('--r', rating);
      plateStars.setAttribute('role', 'img');
      plateStars.setAttribute('aria-label', `Rated ${rating.toFixed(1)} out of 5`);
      if (b.reviewsNumber) plate.querySelector('[data-plate-count]').textContent = `${nf.format(b.reviewsNumber)} Google reviews`;
      const icon = plate.querySelector('[data-plate-icon]');
      const iconUrl = isHttps(b.icon) ? b.icon : b.imageUrl;
      if (icon && isHttps(iconUrl)) {
        icon.addEventListener('load', () => { icon.hidden = false; }, { once: true });
        icon.src = iconUrl;
      }
    }

    summaryCard(items) {
      return h('li', { class: 'crz-card crz-card--summary' },
        h('div', { class: 'crz-card__inner' },
          h('div', { class: 'crz-summary__title', text: 'What customers say' }),
          h('ul', { class: 'crz-summary__list' }, ...items.slice(0, 4).map((t) => h('li', { text: String(t).replace(/[;.]\s*$/, '') }))),
          h('div', { class: 'crz-summary__note', text: 'Summary of our Google reviews' })));
    }

    head(review, when) {
      return h('div', { class: 'crz-card__head' },
        avatar(review.authorName, review.profilePhotoUrl),
        h('div', { class: 'crz-card__who' },
          h('span', { class: 'crz-card__name', text: review.authorName || 'Google user' }),
          h('span', { class: 'crz-card__meta' }, stars(review.rating), h('span', { class: 'crz-card__date', text: when }))),
        this.googleG());
    }

    card(review, index) {
      return h('li', { class: 'crz-card', 'data-index': index },
        h('article', { class: 'crz-card__inner' },
          this.head(review, review.time ? ago(review.time) : ''),
          h('p', { class: 'crz-card__text', text: review.text }),
          h('button', { type: 'button', class: 'crz-card__more', 'data-more': true, hidden: true })));
    }

    // Read more, broken images and the pop-up: both layouts.
    bindCommon() {
      this.track.addEventListener('click', (e) => {
        const button = e.target.closest('[data-more]');
        if (button) this.open(Number(button.closest('[data-index]').dataset.index));
      });
      // A broken avatar (in a card or the pop-up) falls back to the initial underneath it;
      // a broken review photo is dropped.
      this.addEventListener('error', (e) => {
        if (e.target.tagName !== 'IMG') return;
        if (e.target.closest('.crz-avatar')) e.target.remove();
        else e.target.closest('.crz-dialog__photos a')?.remove();
      }, true);
      this.dialog.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    }

    // Grid: rows of cards, Show more adds the same number again.
    renderGrid() {
      const b = this.business;
      const write = this.querySelector('[data-write]');
      if (write && isHttps(b.writeReviewUrl)) { write.href = b.writeReviewUrl; write.hidden = false; }
      const all = this.querySelector('[data-all]');
      if (all && isHttps(b.url)) {
        all.href = b.url;
        if (b.reviewsNumber) all.textContent = `See all ${nf.format(b.reviewsNumber)} reviews on Google`;
      }
      this.moreButton = this.querySelector('[data-show-more]');
      this.moreButton?.addEventListener('click', () => {
        const first = this.shown;
        this.shown += this.perBatch();
        this.applyShown();
        // Keyboard users carry on from the first new review.
        this.track.children[first]?.querySelector('button:not([hidden])')?.focus({ preventScroll: true });
      });
      this.shown = 0;
      this.applyShown();
      this.ro = new ResizeObserver(() => {
        try {
          if (this.track.clientWidth === this.lastWidth) return;
          this.lastWidth = this.track.clientWidth;
          this.applyShown();
        } catch (err) {
          this.fail(err);
        }
      });
      this.ro.observe(this.track);
      document.fonts?.ready.then(() => this.updateMore());
    }

    // Columns come from the stylesheet; rows from the section's settings.
    perBatch() {
      const cols = getComputedStyle(this.track).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
      const rows = matchMedia('(min-width: 769px)').matches
        ? Number(this.dataset.rowsDesktop) || 3
        : Number(this.dataset.rowsMobile) || 4;
      return rows * cols;
    }

    applyShown() {
      const cards = [...this.track.children];
      this.shown = Math.min(cards.length, Math.max(this.shown, this.perBatch()));
      cards.forEach((card, i) => { card.hidden = i >= this.shown; });
      const done = this.shown >= cards.length;
      if (this.moreButton) this.moreButton.hidden = done;
      const all = this.querySelector('[data-all]');
      if (all) all.hidden = !done || all.getAttribute('href') === '#';
      this.updateMore();
    }

    bind() {
      const t = this.track;
      const interacted = () => this.interacted();

      t.addEventListener('touchstart', () => { this.touching = true; interacted(); }, { passive: true });
      t.addEventListener('touchend', () => { this.touching = false; }, { passive: true });
      t.addEventListener('touchcancel', () => { this.touching = false; }, { passive: true });
      t.addEventListener('pointerdown', interacted, { passive: true });
      t.addEventListener('keydown', interacted);
      t.addEventListener('wheel', (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) interacted(); }, { passive: true });

      if ('onscrollend' in window) {
        t.addEventListener('scrollend', () => this.settle());
      } else {
        t.addEventListener('scroll', () => {
          clearTimeout(this.settleTimer);
          this.settleTimer = setTimeout(() => this.settle(), 150);
        }, { passive: true });
      }

      this.bindCommon();

      this.querySelector('[data-prev]')?.addEventListener('click', () => { interacted(); this.prev(); });
      this.querySelector('[data-next]')?.addEventListener('click', () => { interacted(); this.next(); });

      const area = this.querySelector('[data-area]') || this;
      area.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.hovering = true; });
      area.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.hovering = false; });
      // Keyboard focus pauses; the focus a mouse click leaves behind does not.
      this.addEventListener('focusin', (e) => { this.keyboardFocus = e.target.matches(':focus-visible'); });
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.keyboardFocus = false; });

      this.dialog.addEventListener('close', interacted);

      this.visible = true;
      this.io = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; }, { threshold: 0.3 });
      this.io.observe(this);

      this.ro = new ResizeObserver(() => {
        try {
          this.onResize();
        } catch (err) {
          this.fail(err);
        }
      });
      this.ro.observe(t);
    }

    onResize() {
      const width = this.track.clientWidth;
      if (!width || width === this.lastWidth) return;
      this.lastWidth = width;
      if (!this.laidOut) {
        this.laidOut = true;
        this.layout();
      } else {
        this.normalize();
        this.measure();
        this.updateMore();
      }
    }

    layout() {
      const t = this.track;
      const cards = t.children;
      const scrollable = t.scrollWidth > t.clientWidth + 4;
      this.toggleAttribute('data-scrollable', scrollable);
      this.originals = cards.length;
      // Looping needs the set to be at least a screen plus two cards wide, so the row
      // can run half a card past the copy's start before it is moved back.
      const gap = parseFloat(getComputedStyle(t).columnGap) || 0;
      const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : 0;
      this.loop = scrollable && t.scrollWidth + gap >= t.clientWidth + 2 * step;
      if (this.loop) {
        const copies = [...t.children].map((card) => {
          const copy = card.cloneNode(true);
          copy.dataset.clone = '';
          copy.setAttribute('aria-hidden', 'true');
          copy.querySelectorAll('button, a').forEach((n) => { n.tabIndex = -1; });
          return copy;
        });
        t.append(...copies);
      }
      this.measure();
      this.updateMore();
      document.fonts?.ready.then(() => this.updateMore());
      this.schedule();
    }

    measure() {
      const cards = this.track.children;
      if (cards.length > 1) this.step = cards[1].offsetLeft - cards[0].offsetLeft;
      if (this.loop) this.setWidth = cards[this.originals].offsetLeft - cards[0].offsetLeft;
    }

    // Each card shows "Read more" only when its text is cut off or it has photos;
    // a short review with a reply from us gets "See our reply" instead.
    updateMore() {
      for (const card of this.track.querySelectorAll('[data-index]')) {
        if (card.hidden) continue;
        const review = this.reviews[Number(card.dataset.index)];
        const text = card.querySelector('.crz-card__text');
        const button = card.querySelector('[data-more]');
        const cut = text.scrollHeight > text.clientHeight + 1;
        const label = cut || photosOf(review).length ? 'Read more' : replyOf(review) ? 'See our reply' : '';
        button.textContent = label;
        button.hidden = !label;
      }
    }

    jump(left) {
      this.track.scrollLeft = left;
    }

    // Moves back by one set once the row is half a card into the copy. The threshold is
    // past the copy's start on purpose: prev() parks exactly there before sliding back,
    // and the scrollend from that instant move must not undo it.
    normalize() {
      if (this.loop && this.track.scrollLeft > this.setWidth + this.step / 2) this.jump(this.track.scrollLeft - this.setWidth);
    }

    settle() {
      if (!this.touching) this.normalize();
    }

    next() {
      this.normalize();
      this.track.scrollBy({ left: this.step || this.track.clientWidth * 0.8, behavior: 'smooth' });
    }

    prev() {
      if (this.loop && this.track.scrollLeft < this.step / 2) this.jump(this.track.scrollLeft + this.setWidth);
      this.track.scrollBy({ left: -(this.step || this.track.clientWidth * 0.8), behavior: 'smooth' });
    }

    paused() {
      return this.hovering || this.keyboardFocus || this.touching || this.dialog.open || !this.visible || document.hidden;
    }

    schedule(wait = this.delay) {
      clearTimeout(this.timer);
      if (!this.autoplay || !this.loop) return;
      this.timer = setTimeout(() => {
        if (!this.paused()) this.next();
        this.schedule();
      }, wait);
    }

    // After someone swipes, clicks an arrow or closes a review, wait longer before moving on.
    interacted() {
      this.schedule(this.delay * 2);
    }

    open(index) {
      const review = this.reviews[index];
      if (!review) return;
      const photos = photosOf(review);
      const reply = replyOf(review);
      const when = review.time ? df.format(new Date(review.time * 1000)) : '';
      const body = this.dialog.querySelector('[data-body]');
      body.replaceChildren(...[
        this.head(review, when),
        h('p', { class: 'crz-dialog__text', text: review.text }),
        photos.length
          ? h('div', { class: 'crz-dialog__photos' }, ...photos.map((p) =>
              h('a', { href: p.url, target: '_blank', rel: 'noopener' },
                h('img', { src: isHttps(p.thumbnailUrl) ? p.thumbnailUrl : p.url, alt: 'Photo from this review', loading: 'lazy', referrerpolicy: 'no-referrer' }))))
          : null,
        reply
          ? h('div', { class: 'crz-dialog__reply' },
              h('div', { class: 'crz-dialog__reply-title', text: `Reply from ${this.business.name || 'us'}` }),
              h('p', { text: reply }))
          : null,
        isHttps(this.business.url)
          ? h('a', { class: 'crz-dialog__link', href: this.business.url, target: '_blank', rel: 'noopener', text: 'See all our reviews on Google' })
          : null,
      ].filter(Boolean));
      this.dialog.setAttribute('aria-label', `Review by ${review.authorName || 'a Google user'}`);
      this.dialog.showModal();
      this.dialog.scrollTop = 0;
    }
  }

  customElements.define('crz-google-reviews', CrzGoogleReviews);
})();
